import { HtmlTags } from "@components/common/html-tags";
import { Icon, Spinner } from "@components/common/icon";
import { PictrsImage } from "@components/common/pictrs-image";
import { FirstLoadService } from "@services/FirstLoadService";
import { HttpService } from "@services/HttpService";
import { setIsoData, toast, updateMyUserInfo } from "@utils/app";
import { isBrowser } from "@utils/browser";
import { numToSI } from "@utils/helpers";
import { aimooT } from "@utils/aimoo-i18n";
import { IRoutePropsWithFetch } from "@utils/routes";
import { InitialFetchRequest, RouteDataResponse } from "@utils/types";
import classNames from "classnames";
import { Component, InfernoNode } from "inferno";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import {
  Community,
  CommunityFollowerView,
  CommunityModeratorView,
  Person,
} from "lemmy-js-client";
import { NoOptionI18nKeys } from "i18next";
import { communityLink } from "./community-link";
import { scrollMixin } from "../mixins/scroll-mixin";
import { I18NextService } from "../../services";

type MyCommunitiesData = RouteDataResponse<Record<string, never>>;

interface MyCommunitiesState {
  leaveLoadingId?: number;
  /** Local mirrors so leave can update UI immediately. */
  moderates: CommunityModeratorView[];
  follows: CommunityFollowerView[];
  isIsomorphic: boolean;
}

type MyCommunitiesPathProps = Record<string, never>;
type MyCommunitiesRouteProps = RouteComponentProps<MyCommunitiesPathProps>;

export type MyCommunitiesFetchConfig = IRoutePropsWithFetch<
  MyCommunitiesData,
  MyCommunitiesPathProps,
  Record<string, never>
>;

const AVATAR_GRADIENTS = [
  "aimoo-avatar-1",
  "aimoo-avatar-2",
  "aimoo-avatar-3",
  "aimoo-avatar-4",
  "aimoo-avatar-5",
  "aimoo-avatar-6",
];

function avatarGradientClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash + name.charCodeAt(i) * (i + 1)) % AVATAR_GRADIENTS.length;
  }
  return AVATAR_GRADIENTS[hash] ?? AVATAR_GRADIENTS[0];
}

function activityLabel(community: Community): string {
  if (community.users_active_day > 0) {
    return aimooT("active_today");
  }
  if (community.users_active_week > 0) {
    return aimooT("active_week");
  }
  if (community.users_active_month > 0) {
    return aimooT("active_month");
  }
  return aimooT("active_low");
}

function isActiveToday(community: Community): boolean {
  return community.users_active_day > 0;
}

/** Scores may be absent until API/client types expose them. */
function personReceivedScore(person: Person): number {
  const scored = person as Person & {
    post_score?: number;
    comment_score?: number;
  };
  const postScore = Number(scored.post_score);
  const commentScore = Number(scored.comment_score);
  return (
    (Number.isFinite(postScore) ? postScore : 0) +
    (Number.isFinite(commentScore) ? commentScore : 0)
  );
}

@scrollMixin
export class MyCommunities extends Component<
  MyCommunitiesRouteProps,
  MyCommunitiesState
> {
  private isoData = setIsoData<MyCommunitiesData>(this.context);
  state: MyCommunitiesState = {
    moderates: [],
    follows: [],
    isIsomorphic: false,
  };

  constructor(props: MyCommunitiesRouteProps, context: object) {
    super(props, context);
    const mui = this.isoData.myUserInfo;
    this.state = {
      ...this.state,
      moderates: mui?.moderates ?? [],
      follows: mui?.follows ?? [],
      isIsomorphic: FirstLoadService.isFirstLoad,
    };
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      this.syncFromMyUser();
    }
  }

  syncFromMyUser() {
    const mui = this.isoData.myUserInfo;
    this.setState({
      moderates: mui?.moderates ?? [],
      follows: mui?.follows ?? [],
    });
  }

  get documentTitle(): string {
    return `${aimooT("my_communities")} - ${this.isoData.siteRes?.site_view.site.name}`;
  }

  /** Communities I moderate/create (shown as 我创建的). */
  get createdCommunities(): Community[] {
    return this.state.moderates.map(m => m.community);
  }

  /** Communities I joined but do not moderate. */
  get joinedCommunities(): Community[] {
    const modIds = new Set(this.state.moderates.map(m => m.community.id));
    return this.state.follows
      .map(f => f.community)
      .filter(c => !modIds.has(c.id));
  }

  render() {
    const myUserInfo = this.isoData.myUserInfo;
    const created = this.createdCommunities;
    const joined = this.joinedCommunities;
    const person = myUserInfo?.local_user_view.person;

    return (
      <div className="aimoo-my-communities">
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />
        <div className="aimoo-my-communities__inner container-lg px-3">
          <header className="aimoo-my-communities__header">
            <div>
              <h1 className="aimoo-my-communities__title">
                {aimooT("my_communities")}
              </h1>
              <p className="aimoo-my-communities__subtitle">
                {aimooT("my_communities_subtitle")}
              </p>
            </div>
            {myUserInfo && (
              <Link
                to="/create_community"
                className="aimoo-my-communities__create-btn"
              >
                <Icon icon="add" classes="icon-inline" />
                {aimooT("create_community_short")}
              </Link>
            )}
          </header>

          {!myUserInfo ? (
            <div className="aimoo-my-communities__empty aimoo-my-communities__empty--page">
              <p className="aimoo-my-communities__empty-title">
                {aimooT("login_required")}
              </p>
              <p className="aimoo-my-communities__empty-desc">
                {aimooT("login_to_view_my")}
              </p>
              <Link to="/login" className="aimoo-my-communities__create-btn">
                {aimooT("go_login")}
              </Link>
            </div>
          ) : (
            <>
              <section className="aimoo-my-communities__section">
                <h2 className="aimoo-my-communities__section-title">
                  <Icon icon="add" classes="icon-inline" />
                  {aimooT("my_created_communities")}
                </h2>
                {created.length === 0 ? (
                  <div className="aimoo-my-communities__empty">
                    <p className="aimoo-my-communities__empty-title">
                      {aimooT("no_created_yet")}
                    </p>
                    <p className="aimoo-my-communities__empty-desc">
                      {aimooT("no_created_hint")}
                    </p>
                    <Link
                      to="/create_community"
                      className="aimoo-my-communities__create-btn aimoo-my-communities__create-btn--sm"
                    >
                      <Icon icon="add" classes="icon-inline" />
                      {aimooT("create_community_short")}
                    </Link>
                  </div>
                ) : (
                  <div className="aimoo-my-communities__created-list">
                    {created.map(c => this.renderCreatedCard(c))}
                  </div>
                )}
              </section>

              <section className="aimoo-my-communities__section">
                <h2 className="aimoo-my-communities__section-title">
                  <Icon icon="groups" classes="icon-inline" />
                  {aimooT("my_joined_communities")}
                </h2>
                {joined.length === 0 ? (
                  <div className="aimoo-my-communities__empty">
                    <p className="aimoo-my-communities__empty-title">
                      {aimooT("no_joined_other")}
                    </p>
                    <p className="aimoo-my-communities__empty-desc">
                      {aimooT("browse_communities_hint")}
                    </p>
                    <Link
                      to="/"
                      className="aimoo-my-communities__link-btn"
                    >
                      {aimooT("browse_communities")}
                    </Link>
                  </div>
                ) : (
                  <div className="aimoo-my-communities__joined-list">
                    {joined.map(c => this.renderJoinedCard(c))}
                  </div>
                )}
              </section>

              <div className="aimoo-my-communities__stats">
                <div className="aimoo-my-communities__stat">
                  <p className="aimoo-my-communities__stat-value aimoo-my-communities__stat-value--brand">
                    {created.length}
                  </p>
                  <p className="aimoo-my-communities__stat-label">
                    {aimooT("stat_created")}
                  </p>
                </div>
                <div className="aimoo-my-communities__stat">
                  <p className="aimoo-my-communities__stat-value aimoo-my-communities__stat-value--orange">
                    {joined.length}
                  </p>
                  <p className="aimoo-my-communities__stat-label">
                    {aimooT("stat_joined")}
                  </p>
                </div>
                <Link
                  to={
                    person
                      ? `/u/${person.name}`
                      : "/login"
                  }
                  className="aimoo-my-communities__stat aimoo-my-communities__stat--link"
                >
                  <p className="aimoo-my-communities__stat-value aimoo-my-communities__stat-value--success">
                    {person ? numToSI(person.post_count) : "0"}
                  </p>
                  <p className="aimoo-my-communities__stat-label">
                    {aimooT("stat_my_posts")}
                  </p>
                </Link>
                <div className="aimoo-my-communities__stat">
                  <p className="aimoo-my-communities__stat-value aimoo-my-communities__stat-value--purple">
                    {person ? numToSI(personReceivedScore(person)) : "0"}
                  </p>
                  <p className="aimoo-my-communities__stat-label">
                    {aimooT("stat_likes")}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  renderCreatedCard(community: Community): InfernoNode {
    const title = community.title ?? community.name;
    const initial = title.trim().charAt(0).toUpperCase() || "?";
    const path = communityLink(community).link;
    const desc = community.summary?.trim() || aimooT("no_summary");

    return (
      <article key={community.id} className="aimoo-my-communities__created-card">
        <div className="aimoo-my-communities__created-body">
          <div className="aimoo-my-communities__created-info">
            {community.icon ? (
              <PictrsImage
                src={community.icon}
                type="icon"
                nsfw={community.nsfw}
              />
            ) : (
              <span
                className={`aimoo-my-communities__avatar aimoo-my-communities__avatar--lg ${avatarGradientClass(
                  community.name,
                )}`}
              >
                {initial}
              </span>
            )}
            <div className="aimoo-my-communities__created-text">
              <div className="aimoo-my-communities__title-row">
                <h3 className="aimoo-my-communities__card-title">
                  <Link to={path}>{title}</Link>
                </h3>
                <span className="aimoo-my-communities__owner-badge">
                  {aimooT("created_by_me")}
                </span>
              </div>
              <p className="aimoo-my-communities__desc">{desc}</p>
              <div className="aimoo-my-communities__meta">
                <span>
                  <Icon icon="groups" classes="icon-inline" />
                  {aimooT("members_count", {
                    count: numToSI(community.subscribers),
                  })}
                </span>
                <span>
                  <Icon icon="message-square" classes="icon-inline" />
                  {aimooT("posts_count", {
                    count: numToSI(community.posts),
                  })}
                </span>
                <span
                  className={classNames("aimoo-my-communities__activity", {
                    "aimoo-my-communities__activity--live":
                      isActiveToday(community),
                  })}
                >
                  {isActiveToday(community) && (
                    <span className="aimoo-my-communities__activity-dot" />
                  )}
                  {activityLabel(community)}
                </span>
              </div>
            </div>
          </div>
          <div className="aimoo-my-communities__actions">
            <Link
              to={`${path}/settings`}
              className="aimoo-my-communities__btn aimoo-my-communities__btn--ghost"
            >
              {aimooT("manage")}
            </Link>
            <Link
              to={`/create_post?communityId=${community.id}`}
              className="aimoo-my-communities__btn aimoo-my-communities__btn--primary"
            >
              {aimooT("post_action")}
            </Link>
          </div>
        </div>
      </article>
    );
  }

  renderJoinedCard(community: Community): InfernoNode {
    const title = community.title ?? community.name;
    const initial = title.trim().charAt(0).toUpperCase() || "?";
    const path = communityLink(community).link;
    const leaving = this.state.leaveLoadingId === community.id;

    return (
      <article key={community.id} className="aimoo-my-communities__joined-card">
        <div className="aimoo-my-communities__joined-body">
          <div className="aimoo-my-communities__joined-info">
            {community.icon ? (
              <PictrsImage
                src={community.icon}
                type="icon"
                nsfw={community.nsfw}
              />
            ) : (
              <span
                className={`aimoo-my-communities__avatar ${avatarGradientClass(
                  community.name,
                )}`}
              >
                {initial}
              </span>
            )}
            <div className="aimoo-my-communities__joined-text">
              <h4 className="aimoo-my-communities__card-title aimoo-my-communities__card-title--sm">
                <Link to={path}>{title}</Link>
              </h4>
              <p className="aimoo-my-communities__joined-meta">
                {aimooT("members_count", {
                  count: numToSI(community.subscribers),
                })}{" "}
                · {activityLabel(community)}
              </p>
            </div>
          </div>
          <div className="aimoo-my-communities__actions">
            <Link
              to={`/create_post?communityId=${community.id}`}
              className="aimoo-my-communities__btn aimoo-my-communities__btn--primary"
            >
              {aimooT("post_action")}
            </Link>
            <button
              type="button"
              className="aimoo-my-communities__btn aimoo-my-communities__btn--danger"
              disabled={leaving}
              onClick={() => handleLeave(this, community.id)}
            >
              {leaving ? <Spinner /> : aimooT("leave")}
            </button>
          </div>
        </div>
      </article>
    );
  }

  static fetchInitialData = async (
    _req: InitialFetchRequest<MyCommunitiesPathProps, Record<string, never>>,
  ): Promise<MyCommunitiesData> => {
    return {};
  };
}

async function handleLeave(i: MyCommunities, communityId: number) {
  i.setState({ leaveLoadingId: communityId });
  const res = await HttpService.client.followCommunity({
    community_id: communityId as any,
    follow: false,
  });

  if (res.state === "success") {
    const mui = i.isoData.myUserInfo;
    if (mui) {
      mui.follows = mui.follows.filter(f => f.community.id !== communityId);
      updateMyUserInfo(mui);
    }
    i.setState(s => ({
      follows: s.follows.filter(f => f.community.id !== communityId),
      leaveLoadingId: undefined,
    }));
    toast(aimooT("left_community"));
  } else if (res.state === "failed") {
    toast(I18NextService.i18n.t(res.err.name as NoOptionI18nKeys), "danger");
    i.setState({ leaveLoadingId: undefined });
  } else {
    i.setState({ leaveLoadingId: undefined });
  }
}
